<template>
  <AIBasePanel
    auto-scroll
    gradient-header
    hide-home-button
    @close="emit('close')"
  >
    <template
      v-if="appConfig.urls.aiAssistant"
      #toolbarLeft
    >
      <BaseButton
        circle
        data-testid="help-button"
        flat
        :href="appConfig.urls.aiAssistant"
        icon="help"
        new-design
        new-icon
        rel="noopener noreferrer"
        target="_blank"
        type="a"
      >
        <StreemTooltip>
          {{ getLocaleText("ai_assistant_panel.help_tooltip") }}
        </StreemTooltip>
      </BaseButton>
    </template>
    <template #toolbarPrependRight>
      <BaseButton
        circle
        flat
        icon="clock"
        new-design
        new-icon
        @click="openChatHistoryList"
      >
        <StreemTooltip>
          {{ getLocaleText("ai_assistant_panel.history_tooltip") }}
        </StreemTooltip>
      </BaseButton>
    </template>
    <template #header>
      <div class="tw:flex tw:flex-1 tw:flex-col tw:items-center tw:justify-end">
        <div
          class="tw:flex tw:items-center tw:gap-4 tw:pb-4"
          :class="{
            'tw:flex-col': !aiModalMinimise,
          }"
        >
          <div>
            <BaseIcon
              class="tw:text-ai-primary"
              :class="{
                'tw:text-3xl': aiModalMinimise,
                'tw:text-5xl': !aiModalMinimise,
              }"
              icon="ai"
            />
          </div>
          <div
            class="tw:font-bold"
            :class="{
              'tw:text-lg': aiModalMinimise,
              'tw:text-xl': !aiModalMinimise,
            }"
          >
            <i18n-t
              keypath="ai_assistant_panel.hello_message"
              scope="global"
            >
              <template #name>
                {{ currentUser.first_name }}
              </template>
            </i18n-t>
          </div>
        </div>
      </div>
    </template>

    <div class="tw:grid tw:gap-4">
      <AIAssistantBaseChatType
        :delay="500"
        @complete="showOptions = true"
      >
        {{
          getLocaleText("ai_assistant_panel.help_message", {
            platformName: appConfig.platformName,
          })
        }}
      </AIAssistantBaseChatType>
      <AIAssistantBaseChatTransition
        v-if="showOptions"
        appear
        class="tw:grid tw:gap-3"
        data-testid="suggestions-container"
        :stagger-delay="groupsExpanded ? 0 : 250"
      >
        <AIAssistantBaseOptions
          v-for="(group, groupIndex) in sortedOptionGroups"
          :key="group.group"
          :data-index="groupIndex"
          :title="group.label"
        >
          <AIAssistantBaseChatTransition
            appear
            class="tw:grid tw:gap-1"
            :disabled="groupsExpanded"
          >
            <div
              v-for="(option, optionIndex) in group.options"
              :key="optionIndex"
              :data-index="optionIndex"
            >
              <AIAssistantBaseOption
                :icon="option.icon"
                :label="option.label"
                :product="option.product"
                @click="handleOptionClick(option)"
              />
            </div>
          </AIAssistantBaseChatTransition>
        </AIAssistantBaseOptions>
        <div
          v-if="showToggleSuggestions"
          :data-index="visibleGroupsAndOptions"
        >
          <div
            class="tw:cursor-pointer tw:text-sm tw:text-denim-600 tw:hover:text-blue-700 tw:hover:underline"
            data-testid="toggle-suggestions"
            @click="groupsExpanded = !groupsExpanded"
          >
            {{ toggleSuggestionsText }}
          </div>
        </div>
      </AIAssistantBaseChatTransition>
    </div>
  </AIBasePanel>
</template>

<script setup lang="ts">
import { storeToRefs } from "pinia";
import { type Ref, computed, inject, ref } from "vue";

import { track } from "shared/boot/analytics";
import { getLocaleText } from "shared/boot/i18n";
import AIAssistantBaseOption from "shared/components/AI//AIAssistant/AIAssistantBaseOption.vue";
import AIAssistantBaseChatTransition from "shared/components/AI/AIAssistant/AIAssistantBaseChatTransition.vue";
import AIAssistantBaseChatType from "shared/components/AI/AIAssistant/AIAssistantBaseChatType.vue";
import AIAssistantBaseOptions from "shared/components/AI/AIAssistant/AIAssistantBaseOptions.vue";
import AIBasePanel from "shared/components/AI/AIBasePanel.vue";
import { BaseButton, BaseIcon } from "shared/components/base";
import useAIAssistantOptions, {
  type AIAssistantOption,
} from "shared/composables/useAIAssistantOptions";
import type { AppConfig } from "shared/services/appConfig/types";
import useAIStore from "shared/stores/ai";
import { useGlobalStore } from "shared/stores/global";
import useUserStore from "shared/stores/user";

const emit = defineEmits(["close"]);

const isMobile = inject<boolean>("isMobile", false);

const aiStore = useAIStore();
const { aiAssistantPrompt, aiModalMinimise } = storeToRefs(aiStore);
const { openAIModal } = aiStore;

const userStore = useUserStore();
const { currentUser } = storeToRefs(userStore);

const showOptions = ref<boolean>(false);

const {
  sortedOptionGroups,
  groupsExpanded,
  optionGroups,
  visibleGroupsAndOptions,
  hasStreamsCredits,
} = useAIAssistantOptions(isMobile);

const { appConfig } = storeToRefs(useGlobalStore()) as unknown as {
  appConfig: Ref<AppConfig>;
};

async function handleOptionClick(option: AIAssistantOption) {
  track("Select Template Button in Assistant Panel", { prompt: option.track });

  const canCreateStreams = await hasStreamsCredits(option);

  if (!canCreateStreams) {
    return;
  }

  if (option.panel) {
    openAIModal(option.panel);
  } else {
    aiAssistantPrompt.value = option;
    openAIModal("AIGeneralAssistantPanel");
  }
}

function openChatHistoryList() {
  openAIModal("AIChatHistoryListPanel");
}

const showToggleSuggestions = computed(() => optionGroups.value.length > 1);

const toggleSuggestionsText = computed(() =>
  groupsExpanded.value
    ? getLocaleText("ai_assistant_panel.hide_suggestions")
    : getLocaleText("ai_assistant_panel.more_suggestions")
);
</script>

<style scoped lang="scss">
@import "shared/css/components/ai.scss";
</style>
